import { useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { CssBaseline, ThemeProvider } from '@mui/material';
import { ReactKeycloakProvider } from '@react-keycloak/web';

import DrawerNotifications from 'components/ui/drawerNotifications/DrawerNotifications';
import SnackBarComponent from 'components/ui/snackBar/SnackBar';

import { loginSSOUseCase } from 'useCases/Auth/loginSSOUseCase';

import {
  getKeycloakClientId,
  getKeycloakRealm,
  getKeycloakUrl,
  useSsoSdkBypass,
} from 'services/HostService/HostService';

import { keycloak, keycloakInitOptions } from './keycloak';
import Routes from './Routes';
import { theme } from './styles/theme';

function AppShell() {
  return (
    <DndProvider backend={HTML5Backend}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Routes />
        <DrawerNotifications />
        <SnackBarComponent />
      </ThemeProvider>
    </DndProvider>
  );
}

const PKCE_VERIFIER_STORAGE_KEY = 'sso_pkce_verifier';
const PKCE_STATE_STORAGE_KEY = 'sso_pkce_state';

const getCallbackStorageValue = (key: string): string | null => {
  return sessionStorage.getItem(key) ?? localStorage.getItem(key);
};

const clearBypassCallbackArtifacts = () => {
  sessionStorage.removeItem(PKCE_VERIFIER_STORAGE_KEY);
  sessionStorage.removeItem(PKCE_STATE_STORAGE_KEY);
  localStorage.removeItem(PKCE_VERIFIER_STORAGE_KEY);
  localStorage.removeItem(PKCE_STATE_STORAGE_KEY);
  window.history.replaceState(null, document.title, window.location.pathname);
};

export default function App() {
  const bypassSdkValidation = useSsoSdkBypass();
  const [isProcessingSsoCallback, setIsProcessingSsoCallback] = useState(false);

  useEffect(() => {
    if (!bypassSdkValidation) return;

    const keycloakUrl = getKeycloakUrl();
    const realm = getKeycloakRealm();
    const clientId = getKeycloakClientId();

    if (!keycloakUrl || !realm || !clientId) return;

    const queryParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const getParam = (name: string): string | null => {
      return queryParams.get(name) ?? hashParams.get(name);
    };

    const error = getParam('error');
    if (error) {
      clearBypassCallbackArtifacts();
      return;
    }

    const code = getParam('code');
    if (!code) return;

    const callbackState = getParam('state');
    const expectedState = getCallbackStorageValue(PKCE_STATE_STORAGE_KEY);
    const codeVerifier = getCallbackStorageValue(PKCE_VERIFIER_STORAGE_KEY);

    if (!callbackState || !expectedState || callbackState !== expectedState || !codeVerifier) {
      clearBypassCallbackArtifacts();
      return;
    }

    const exchangeCodeForToken = async () => {
      try {
        setIsProcessingSsoCallback(true);

        const tokenEndpoint = `${keycloakUrl.replace(
          /\/$/,
          '',
        )}/realms/${realm}/protocol/openid-connect/token`;
        const body = new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: clientId,
          code,
          redirect_uri: window.location.origin,
          code_verifier: codeVerifier,
        });

        const response = await fetch(tokenEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: body.toString(),
        });

        if (!response.ok) {
          throw new Error('Failed to exchange authorization code for access token');
        }

        const tokenResponse = await response.json();
        const accessToken = tokenResponse?.access_token;
        if (!accessToken) {
          throw new Error('Missing access_token in token response');
        }

        clearBypassCallbackArtifacts();
        await loginSSOUseCase.execute({ token: accessToken });
      } catch (err) {
        clearBypassCallbackArtifacts();
        setIsProcessingSsoCallback(false);
      }
    };

    exchangeCodeForToken();
  }, [bypassSdkValidation]);

  if (isProcessingSsoCallback) {
    return null;
  }

  if (bypassSdkValidation) {
    return <AppShell />;
  }

  return (
    <ReactKeycloakProvider
      authClient={keycloak}
      autoRefreshToken={false}
      onTokens={v => {
        if (v?.token) {
          loginSSOUseCase.execute({
            token: v.token,
          });
        }
      }}
      initOptions={{
        ...keycloakInitOptions,
      }}
    >
      <AppShell />
    </ReactKeycloakProvider>
  );
}
